import React from "react";
import { Package, Truck } from "lucide-react";
import useSiteContent from "@/lib/useSiteContent";
import { formatPrice } from "./_shared";
import PackageCta from "./PackageCta";

/**
 * Reusable SKU card for install + data porting.
 *
 * Every card carries a `PackageCta`: before that, the card was name + price + features and
 * nothing clickable, while the plan cards directly above it had a working Choose button
 * (BUG-076, BUG-206). `sku.name`, `sku.tagline` and `sku.features` are catalogue data the
 * server owns; every sentence this component adds around them goes through the locale
 * layer, and no amount is written here — `sku.price` is the only authority.
 */
function SkuCard({ sku, currency, accent }) {
  const { text } = useSiteContent();
  const price = sku.price?.[currency] ?? 0;
  const formatted = formatPrice(price, currency);
  return (
    <div
      data-testid={`sku-${sku.sku_id}`}
      className={`rounded-2xl p-6 border flex flex-col ${
        sku.featured
          ? accent === "gold"
            ? "bg-gradient-to-br from-sol-gold/10 to-transparent border-sol-gold/40"
            : "bg-gradient-to-br from-sol-sage/15 to-transparent border-sol-sage/40"
          : "bg-white border-sol-midnight/8"
      }`}
    >
      {sku.featured && (
        <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent === "gold" ? "text-sol-gold" : "text-sol-sage"}`}>
          ★ {text("landing.pkg.mostPopular")}
        </div>
      )}
      <h4 className="font-sol font-semibold text-base text-sol-midnight">{sku.name}</h4>
      {sku.tagline && <p className="text-xs italic text-sol-midnight/55 mt-1 mb-3">"{sku.tagline}"</p>}
      <div className="mt-3 mb-4 font-sol font-semibold text-3xl text-sol-midnight">
        {price === 0
          ? text("landing.pkg.free")
          : (sku.is_starting_at ? text("landing.pkg.fromPrice", { price: formatted }) : formatted)}
        {price !== 0 && <span className="text-xs text-sol-midnight/50 ml-1 font-normal">{text("landing.pkg.oneTime")}</span>}
      </div>
      <ul className="space-y-1.5 text-xs text-sol-midnight/65 flex-1">
        {sku.features.map((f) => (
          <li key={`sku-feat-${sku.sku_id}-${f}`} className="flex gap-1.5">
            <span className="text-sol-gold">·</span>
            {f}
          </li>
        ))}
      </ul>
      <PackageCta itemId={sku.sku_id} itemName={sku.name} currency={currency} tone={accent === "gold" ? "midnight" : "sage"} />
    </div>
  );
}

export default function TurnkeyAndPorting({ installSkus, portingSkus, currency }) {
  const { text } = useSiteContent();
  return (
    <section className="py-28 bg-sol-ivory">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.24em] text-sol-gold mb-4">{text("landing.turnkey.eyebrow")}</div>
          <h2 className="font-display text-4xl lg:text-5xl text-sol-midnight leading-tight">
            {text("landing.turnkey.titleA")} <span className="italic">{text("landing.turnkey.titleB")}</span>
          </h2>
          <p className="mt-6 text-sol-midnight/60">{text("landing.turnkey.subtitle")}</p>
        </div>

        {/* Where the purchase actually happens — the cards below lead to signup, not to a
            checkout, because there is none outside the practice dashboard. */}
        <div
          data-testid="turnkey-purchase-note"
          className="max-w-3xl mx-auto mb-14 rounded-2xl bg-white border border-sol-gold/30 px-6 py-4 text-center text-sm text-sol-midnight/80"
        >
          {text("landing.turnkey.purchaseNote")}
        </div>

        {/* Install SKUs */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-5 h-5 text-sol-gold" />
            <h3 className="font-sol font-semibold text-xl text-sol-midnight">{text("landing.turnkey.install")}</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {installSkus.map((sku) => (
              <SkuCard key={sku.sku_id} sku={sku} currency={currency} accent="gold" />
            ))}
          </div>
        </div>

        {/* Data Porting */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Truck className="w-5 h-5 text-sol-gold" />
            <h3 className="font-sol font-semibold text-xl text-sol-midnight">{text("landing.turnkey.porting")}</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {portingSkus.map((sku) => (
              <SkuCard key={sku.sku_id} sku={sku} currency={currency} accent="sage" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
